import { useAppContext, useDataInjector } from "@ciq/shared/hooks/context"
import { useMemo, useRef } from "react"
import { ErrorToast } from "@ciq/shared/utils/toast"
import {
  fetchMappings,
  createMapping,
  updateMapping,
  deleteMapping,
  createColumn,
  updateColumn,
  deleteColumn,
} from "./api"

export const SettingsMappingsKey = {
  KEY: "settings_mappings",
  DEFAULT_DATA: {
    isLoaded: false,
    data: {
      datasets: [],
      groups: [],
      sources: [],
    },
  },
}

/**
 * @typedef {Object} UseDataSourceMappingResult
 * @property {boolean} isLoaded - Boolean indicating if the mapping data is currently loaded
 * @property {Array<Object>} datasets - Array of configured dataset mappings with column definitions
 * @property {boolean} hasDatasets - Boolean indicating if any datasets are configured
 * @property {Array<string>} groups - Array of available mapping group names
 * @property {boolean} hasGroups - Boolean indicating if any mapping groups exist
 * @property {Array<Object>} sources - Array of available datasource schemas with tables and columns
 * @property {boolean} hasSources - Boolean indicating if any datasources are available
 * @property {Array<string>} claimedColumnIds - Array of column IDs already mapped across all datasets
 * @property {Array<string>} existingDisplayNames - Array of lowercase display names already in use
 * @property {Object} actions - Collection of functions for managing datasource mappings
 * @property {() => void} actions.reset - Reset the mapping data in the store to default values
 * @property {() => void} actions.getMappings - Fetch datasource mappings from the API
 * @property {(newData: Object, callback?: Function) => void} actions.addMapping - Add a new datasource mapping
 * @property {(datasourceId: string, newData: Object) => void} actions.editMapping - Edit an existing datasource mapping
 * @property {(datasourceId: string) => void} actions.deleteMapping - Delete a datasource mapping
 * @property {(datasourceId: string, newData: Object, callback: Function) => void} actions.addColumn - Add a column mapping to a datasource
 * @property {(datasourceId: string, columnId: string, newData: Object) => void} actions.editColumn - Edit a column mapping
 * @property {(datasourceId: string, columnName: string) => void} actions.deleteColumn - Delete a column mapping
 */

/**
 * Custom hook for managing datasource mappings and column configurations.
 * Provides access to datasets, sources, groups, and computed filtering arrays for efficient column selection.
 * Does not automatically load data - use actions.getMappings() to fetch data.
 *
 * @returns {UseDataSourceMappingResult} Hook result object with data, loading state, computed arrays, and actions
 */
export function useDataSourceMapping() {
  const { addLoadingId, removeLoadingId, schoolId } = useAppContext()
  const { data: mappingData, setData, reload } = useDataInjector(SettingsMappingsKey)

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  const setDataRef = useRef(setData)
  const resetRef = useRef(reload)

  const actions = useMemo(
    () => ({
      reset: resetRef.current,
      getMappings: async () => {
        const loadingId = "source_datasource_mapping_load"
        addLoadingIdRef.current(loadingId)
        setDataRef.current(prev => ({ ...prev, isLoaded: false }))

        const controller = new AbortController()
        try {
          const data = await fetchMappings(schoolId, controller.signal)
          setDataRef.current(prev => ({ ...prev, data, isLoaded: true }))
        } catch (e) {
          setDataRef.current(prev => ({ ...prev, isLoaded: true }))

          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to fetch Datasources")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
      addMapping: async (newData, callback) => {
        const loadingId = "datasourceMapping#create"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await createMapping(schoolId, newData, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
          callback && callback()
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to add Datasource")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
      editMapping: async (datasourceId, newData) => {
        const loadingId = "datasourceMapping#update"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await updateMapping(schoolId, datasourceId, newData, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to update Datasource")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
      deleteMapping: async datasourceId => {
        const loadingId = "datasourceMapping#delete"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await deleteMapping(schoolId, datasourceId, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to delete Datasource")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },

      addColumn: async (datasourceId, newData, callback) => {
        const loadingId = "datasourceColumn#create"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await createColumn(schoolId, datasourceId, newData, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
          callback && callback(true)
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to add Datasource Column")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
      editColumn: async (datasourceId, columnId, newData) => {
        const loadingId = "datasourceColumn#update"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await updateColumn(schoolId, datasourceId, columnId, newData, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to update Datasource Column")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
      deleteColumn: async (datasourceId, columnName) => {
        const loadingId = "datasourceColumn#delete"
        addLoadingIdRef.current(loadingId)

        const controller = new AbortController()
        try {
          const data = await deleteColumn(schoolId, datasourceId, columnName, controller.signal)
          setDataRef.current(prev => ({ ...prev, data }))
        } catch (e) {
          if (e.handled) return

          console.error(e.error)
          ErrorToast("Unable to delete Datasource Column")
        } finally {
          removeLoadingIdRef.current(loadingId)
        }
      },
    }),
    [schoolId]
  )

  return useMemo(() => {
    const datasets = mappingData.data.datasets || []
    const groups = mappingData.data.groups || []
    const sources = mappingData.data.sources || []

    const claimedColumnIds = datasets.flatMap(dataset =>
      (dataset.columns || []).map(col => col.column?.id).filter(Boolean)
    )

    const existingDisplayNames = datasets.flatMap(dataset =>
      (dataset.columns || []).map(col => col.display_name?.toLowerCase()).filter(Boolean)
    )

    return {
      isLoaded: mappingData.isLoaded,

      datasets,
      hasDatasets: datasets.length > 0,

      groups,
      hasGroups: groups.length > 0,

      sources,
      hasSources: sources.length > 0,

      claimedColumnIds,
      existingDisplayNames,

      actions,
    }
  }, [mappingData, actions])
}
